/**
 * IndexedDB draft persistence for the Bulk Photo Sorter.
 *
 * Why IDB instead of localStorage:
 *   Photos are stored as raw `File` blobs (10s of MB per shoot). localStorage
 *   is limited to ~5MB and only accepts strings. IDB natively stores Blobs
 *   and comfortably handles hundreds of MB.
 *
 * Data shape:
 *   { clusters: [...], unassigned: [...], gap: number, updatedAt: number }
 *   Each photo entry is  { id, name, size, takenAt, file: Blob }
 *   (previewUrl is re-created after load via URL.createObjectURL.)
 *
 * A single record is stored under the fixed key "current" — one draft per
 * browser profile. If the user starts a second sort on another device, they
 * just don't see this device's draft, which is intentional for privacy.
 */

const DB_NAME = "mp_bulk_photo_sorter";
const STORE = "draft";
const HISTORY_STORE = "history";
const KEY = "current";
// v2 adds the `history` object store used by the Rescue History panel to hold
// the last MAX_HISTORY_SLOTS drafts through Clear/Export events. Existing v1
// drafts survive the upgrade because we only *add* a new store.
const DB_VERSION = 2;
// Cap on how many old drafts we retain. Each snapshot holds every raw File
// blob so 3 × ~500MB drafts × ~1GB total is the realistic worst case; more
// than that would push Safari's IDB quota. Three slots covers "mis-clicked
// clear" for weeks without eating the whole disk budget.
const MAX_HISTORY_SLOTS = 3;

let dbPromise = null;
const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("no indexeddb")); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      if (!db.objectStoreNames.contains(HISTORY_STORE)) {
        // keyPath 'id' → each snapshot supplies its own timestamp-based id so
        // we can sort by recency without a secondary index.
        db.createObjectStore(HISTORY_STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
};

const withStore = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    Promise.resolve(fn(store)).then((r) => { result = r; });
  });
};

// ---------------------------------------------------------------- serialise
/**
 * Strip the transient previewUrl and cluster-derived shape so we only persist
 * the raw ingredients. On load we re-derive previewUrl via URL.createObjectURL.
 */
const serialisePhoto = (p) => ({
  id: p.id,
  name: p.name,
  size: p.size,
  takenAt: p.takenAt || null,
  file: p.file,
});
const serialiseCluster = (c) => ({
  id: c.id,
  targetItemId: c.targetItemId || null,
  savedAt: c.savedAt || null,
  photos: c.photos.map(serialisePhoto),
});

export async function saveDraft({ clusters, unassigned, gap }) {
  try {
    const payload = {
      clusters: clusters.map(serialiseCluster),
      unassigned: unassigned.map(serialisePhoto),
      gap,
      updatedAt: Date.now(),
    };
    await withStore("readwrite", (s) => s.put(payload, KEY));
    return true;
  } catch {
    return false;
  }
}

export async function loadDraft() {
  try {
    const raw = await new Promise((resolve, reject) => {
      openDb().then((db) => {
        const tx = db.transaction(STORE, "readonly");
        const req = tx.objectStore(STORE).get(KEY);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
      }, reject);
    });
    if (!raw) return null;
    // Re-hydrate previewUrl for every stored blob.
    const rehydratePhoto = (p) => ({
      ...p,
      previewUrl: URL.createObjectURL(p.file),
    });
    return {
      clusters: (raw.clusters || []).map((c) => ({ ...c, photos: c.photos.map(rehydratePhoto) })),
      unassigned: (raw.unassigned || []).map(rehydratePhoto),
      gap: raw.gap,
      updatedAt: raw.updatedAt,
    };
  } catch {
    return null;
  }
}

export async function clearDraft() {
  try {
    await withStore("readwrite", (s) => s.delete(KEY));
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------- Rescue History
// A small ring buffer of previously-cleared/exported drafts. Purpose: undo a
// mis-clicked "Clear Draft" weeks after the fact, even after the tab has been
// closed and reopened. Stored in a separate object store so live-draft reads
// stay fast (no need to scan history rows).

const historyStore = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(HISTORY_STORE, mode);
    const store = tx.objectStore(HISTORY_STORE);
    let result;
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    Promise.resolve(fn(store)).then((r) => { result = r; });
  });
};

/**
 * Snapshot the current sorter state into history. Called before a Clear Draft
 * or a Download Draft so the state is recoverable even after IndexedDB's live
 * `current` slot has been wiped or the tab has been reopened.
 *
 * @param {object} state
 * @param {Array}  state.clusters
 * @param {Array}  state.unassigned
 * @param {number} state.gap
 * @param {"clear"|"export"|"manual"} source  Why the snapshot was taken.
 * @returns {Promise<boolean>}
 */
export async function saveHistorySnapshot({ clusters, unassigned, gap, source = "manual" }) {
  try {
    const draftClusters = (clusters || []).filter((c) => !c.savedAt);
    const totalPhotos =
      draftClusters.reduce((n, c) => n + c.photos.length, 0) + (unassigned || []).length;
    if (totalPhotos === 0) return false; // nothing worth remembering
    const id = `snap_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const payload = {
      id,
      createdAt: Date.now(),
      source,
      clusters: draftClusters.map(serialiseCluster),
      unassigned: (unassigned || []).map(serialisePhoto),
      gap,
      totalPhotos,
      totalClusters: draftClusters.length,
    };
    await historyStore("readwrite", (s) => s.put(payload));
    // Enforce the cap: keep only the newest MAX_HISTORY_SLOTS.
    const all = await listHistorySnapshots();
    if (all.length > MAX_HISTORY_SLOTS) {
      const toRemove = all.slice(MAX_HISTORY_SLOTS); // list is newest-first
      await historyStore("readwrite", (s) => {
        toRemove.forEach((r) => s.delete(r.id));
      });
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Return metadata for every history slot, newest first. Does NOT return the
 * raw File blobs — the panel only needs id/createdAt/counts to render.
 */
export async function listHistorySnapshots() {
  try {
    const rows = await new Promise((resolve, reject) => {
      openDb().then((db) => {
        const tx = db.transaction(HISTORY_STORE, "readonly");
        const req = tx.objectStore(HISTORY_STORE).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      }, reject);
    });
    return rows
      .map((r) => ({
        id: r.id,
        createdAt: r.createdAt,
        source: r.source,
        totalPhotos: r.totalPhotos,
        totalClusters: r.totalClusters,
      }))
      .sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

/**
 * Read one history slot and re-hydrate its File blobs into a ready-to-use
 * sorter state. Photo previewUrls are freshly generated so tiles paint
 * immediately after restore.
 */
export async function restoreHistorySnapshot(id) {
  try {
    const raw = await new Promise((resolve, reject) => {
      openDb().then((db) => {
        const tx = db.transaction(HISTORY_STORE, "readonly");
        const req = tx.objectStore(HISTORY_STORE).get(id);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
      }, reject);
    });
    if (!raw) return null;
    const rehydratePhoto = (p) => ({ ...p, previewUrl: URL.createObjectURL(p.file) });
    return {
      clusters: (raw.clusters || []).map((c) => ({ ...c, photos: c.photos.map(rehydratePhoto) })),
      unassigned: (raw.unassigned || []).map(rehydratePhoto),
      gap: raw.gap,
      createdAt: raw.createdAt,
      source: raw.source,
    };
  } catch {
    return null;
  }
}

export async function deleteHistorySnapshot(id) {
  try {
    await historyStore("readwrite", (s) => s.delete(id));
    return true;
  } catch {
    return false;
  }
}
